import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="legal-meta">Effective Date: February 26, 2026 &middot; Version 1.0</p>

        <section>
          <p>
            This Privacy Policy describes how <strong>QorVertex LLC</strong> ("we," "us," or "our") collects,
            uses, and protects your information when you use the AmpNebula website, applications, and services
            (collectively, the "Service").
          </p>
          <p>By using the Service, you agree to the collection and use of information as described in this policy.</p>
        </section>

        <section>
          <h2>1. Information We Collect</h2>

          <h3>1.1 Account Information</h3>
          <p>When you create an account, we collect:</p>
          <ul>
            <li>Username and display name</li>
            <li>Email address</li>
            <li>Password (stored as a salted, one-way hash — we never store your plain-text password)</li>
          </ul>

          <h3>1.2 Collection &amp; Content Data</h3>
          <p>When you use the Service, you may voluntarily provide:</p>
          <ul>
            <li>Amplifier collection details (manufacturer, model, year, condition, serial number, photos)</li>
            <li>Amplifier valuations requested and generated</li>
            <li>Discussion forum posts and replies</li>
            <li>Showcase gallery photos, titles, and captions</li>
            <li>Feedback submissions (category, description, page context)</li>
          </ul>

          <h3>1.3 Automatically Collected Information</h3>
          <ul>
            <li><strong>Activity logs:</strong> Page views, searches, feature usage, and other interactions with the Service</li>
            <li><strong>Device &amp; browser information:</strong> Browser type, operating system, screen resolution, and user agent string</li>
            <li><strong>IP address:</strong> Used for security (rate limiting, abuse prevention), approximate geographic analytics, and legal compliance</li>
            <li><strong>Cookies &amp; local storage:</strong> We use browser local storage to maintain your authentication session (JWT token) and user preferences. We do not use third-party tracking cookies.</li>
          </ul>

          <h3>1.4 Feedback Screenshots</h3>
          <p>
            When you submit feedback through the in-app widget, the Service may automatically capture a screenshot
            of the current page to help us understand and reproduce issues. This screenshot is stored with your
            feedback submission. You can remove the screenshot before submitting.
          </p>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li><strong>Provide the Service:</strong> Maintain your account, display your collection, generate valuations, facilitate discussions</li>
            <li><strong>Improve the Service:</strong> Analyze usage patterns, identify bugs, prioritize features</li>
            <li><strong>Communicate with you:</strong> Send account verification emails, password resets, subscription notifications, and service updates</li>
            <li><strong>Ensure security:</strong> Detect and prevent fraud, abuse, and unauthorized access</li>
            <li><strong>Comply with legal obligations:</strong> Respond to legal requests and enforce our Terms of Service</li>
          </ul>
          <p>We do <strong>not</strong> sell your personal information to third parties.</p>
        </section>

        <section>
          <h2>3. Third-Party Services</h2>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Purpose</th>
                <th>Data Shared</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Amazon Web Services (AWS)</strong></td>
                <td>Hosting and infrastructure</td>
                <td>All Service data is stored on AWS servers in the US</td>
              </tr>
              <tr>
                <td><strong>Stripe</strong></td>
                <td>Payment processing</td>
                <td>Email, name, payment method (handled directly by Stripe — we do not store credit card numbers)</td>
              </tr>
              <tr>
                <td><strong>Google Analytics</strong></td>
                <td>Website analytics</td>
                <td>Anonymized usage data, page views, session information</td>
              </tr>
              <tr>
                <td><strong>Google Gemini AI</strong></td>
                <td>AI-generated amplifier descriptions and valuations</td>
                <td>Amplifier names, specifications, and publicly available market data</td>
              </tr>
              <tr>
                <td><strong>Cloudflare</strong></td>
                <td>DNS, CDN, and DDoS protection</td>
                <td>IP addresses, request headers</td>
              </tr>
            </tbody>
          </table>
          <p>Each third-party service operates under its own privacy policy. We encourage you to review their policies.</p>
        </section>

        <section>
          <h2>4. Data Retention</h2>
          <ul>
            <li><strong>Account data:</strong> Retained as long as your account is active. If you delete your account, we will remove your personal information within 30 days.</li>
            <li><strong>Activity logs:</strong> Retained for up to 24 months for analytics purposes. Older logs are automatically purged or anonymized.</li>
            <li><strong>Discussion posts:</strong> Retained as part of the public record. If you delete your account, your posts will be attributed to "Deleted User" rather than removed.</li>
            <li><strong>Feedback submissions:</strong> Retained until resolved and archived by an administrator.</li>
            <li><strong>Valuation data:</strong> Aggregated and anonymized valuation data may be retained indefinitely for market analytics purposes.</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>We implement reasonable technical and organizational measures to protect your information, including:</p>
          <ul>
            <li>Encrypted passwords (bcrypt hashing with salt)</li>
            <li>HTTPS encryption for all data in transit</li>
            <li>Database access restricted to authorized services</li>
            <li>Regular security reviews</li>
          </ul>
          <p>However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.</p>
        </section>

        <section>
          <h2>6. Your Rights &amp; Choices</h2>
          <h3>6.1 Access &amp; Correction</h3>
          <p>You can view and update your account information at any time through your account settings.</p>

          <h3>6.2 Account Deletion</h3>
          <p>
            You may request deletion of your account by contacting us at{' '}
            <a href="mailto:privacy@ampnebula.com">privacy@ampnebula.com</a>. Upon deletion, your personal
            information will be removed and discussion posts will be anonymized.
          </p>

          <h3>6.3 Data Export</h3>
          <p>
            You may request a copy of your personal data by contacting us at{' '}
            <a href="mailto:privacy@ampnebula.com">privacy@ampnebula.com</a>. We will provide your data in a
            commonly used electronic format within 30 days.
          </p>

          <h3>6.4 Communication Preferences</h3>
          <p>
            You may opt out of non-essential email communications at any time. Transactional emails (account
            verification, password resets, billing) cannot be opted out of while your account is active.
          </p>
        </section>

        <section>
          <h2>7. California Privacy Rights (CCPA)</h2>
          <p>If you are a California resident, you have the right to:</p>
          <ul>
            <li><strong>Know</strong> what personal information we collect, use, and disclose</li>
            <li><strong>Delete</strong> your personal information (subject to certain exceptions)</li>
            <li><strong>Opt-out</strong> of the sale of your personal information (we do not sell personal information)</li>
            <li><strong>Non-discrimination</strong> for exercising your privacy rights</li>
          </ul>
          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:privacy@ampnebula.com">privacy@ampnebula.com</a>.
          </p>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <p>
            The Service is not directed to children under the age of 13. We do not knowingly collect personal
            information from children under 13. If we discover that we have collected information from a child
            under 13, we will promptly delete it.
          </p>
        </section>

        <section>
          <h2>9. International Users</h2>
          <p>
            The Service is operated from the United States. If you access the Service from outside the US, your
            information will be transferred to and processed in the United States, where data protection laws may
            differ from your jurisdiction.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the effective date and
            version number. Registered users will be notified of material changes by email and/or in-app notice.
          </p>
        </section>

        <section>
          <h2>11. Contact</h2>
          <p>
            For privacy-related questions or requests, contact us at{' '}
            <a href="mailto:privacy@ampnebula.com">privacy@ampnebula.com</a>.
          </p>
          <p className="legal-entity">
            <strong>QorVertex LLC</strong>
          </p>
        </section>

        <div className="legal-nav">
          <Link to="/terms">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
